import FormItem from "components/Form/FormItem";
import NcInputNumber from "components/NcInputNumber/NcInputNumber";
import moment, { Moment } from "moment";
import React, { FC, useState, useEffect } from "react";
import { DateRangePicker, FocusedInputShape } from "react-dates";
import Input from "shared/Input/Input";
import usePlacesAutocomplete, {
    getGeocode,
    getLatLng,
} from 'use-places-autocomplete';
import { Link } from "react-router-dom";
import ncNanoId from "utils/ncNanoId";
import Loader from "react-loader-spinner";
import { useDispatch } from "react-redux";
import { updateFormTrip, updateFormTripMass } from "functions/redux/listing/action";
import { useTranslation } from "react-i18next";
import Geocode from "react-geocode";
import { getCountryByCode } from "functions/redux/country/action";
import { showAlert } from "functions/redux/general/action";
import { modalContent } from "containers/PageSignUp/PageSignUp";
import GoogleMapReact from "google-map-react";
import LocationMarker from "components/AnyReactComponent/LocationMarker";

interface OpenStepOneProps {
    form: any
}
Geocode.setApiKey(process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "");

const OpenStepOne: FC<OpenStepOneProps> = ({ form }) => {
    const [dateRange, setDateRange] = useState<{ startDate: null | Moment, endDate: null | Moment }>({
        startDate: form?.startTripDate ? moment(form.startTripDate) : null,
        endDate: form?.endTripDate ? moment(form.endTripDate) : null,
    });

    const [focusedInput, setFocusedInput] = useState<null | FocusedInputShape>(null);
    const {
        ready,
        value: place,
        suggestions: { status, data: suggestionsData },
        setValue: setPlace,
        clearSuggestions,
    } = usePlacesAutocomplete({
        defaultValue: form?.meetingAddress ?? "",
        debounce: 300,
    });

    const [placeLoading, setPlaceLoading] = useState(false);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [lat, setLat] = useState(form?.meetingLat ? parseFloat(form.meetingLat) : 0);
    const [lng, setLng] = useState(form?.meetingLng ? parseFloat(form.meetingLng) : 0);
    const [mapCenter, setMapCenter] = useState({
        lat: form?.meetingLat ? parseFloat(form.meetingLat) : 0,
        lng: form?.meetingLng ? parseFloat(form.meetingLng) : 0
    });
    const [defLat, setDefLat] = useState(form?.meetingLat ? parseFloat(form.meetingLat) : 0);
    const [defLng, setDefLng] = useState(form?.meetingLng ? parseFloat(form.meetingLng) : 0);

    useEffect(() => {
        if (form?.meetingAddress && form.meetingAddress !== place) {
            setPlace(form.meetingAddress, false);
        }
    }, [form?.meetingAddress]);

    useEffect(() => {
        if ((form == null || form?.id == null) && (lat === 0 && lng === 0)) {
            navigator.geolocation.getCurrentPosition(function (position) {
                setLat(position.coords.latitude);
                setLng(position.coords.longitude);
                setDefLat(position.coords.latitude);
                setDefLng(position.coords.longitude);
            });
        }
    })

    const handleSelect = (address: string) => {
        setPlaceLoading(true);
        setPlace(address, false);
        clearSuggestions();
        getGeocode({ address })
            .then(async (results: any) => {
                var latLng = await getLatLng(results[0]);
                setLat(latLng.lat);
                setLng(latLng.lng);
                setMapCenter({
                    lat: latLng.lat,
                    lng: latLng.lng
                });
                meetingAddressUpdate(latLng.lat, latLng.lng);
                setPlaceLoading(false);
            })
            .catch((error: any) => {
                console.error('Error', error);
                setPlaceLoading(false);
            });
    };

    const meetingAddressUpdate = (lat: number, lng: number,) => {
        Geocode.fromLatLng(lat, lng).then(
            async (response: any) => {
                const address = response.results[0].formatted_address;
                setPlace(address, false);
                let country;
                response.results.forEach((item: any, k: any) => {
                    if (item.types[0] === 'country') {
                        country = item.address_components[0].short_name;
                    }
                });

                if (country !== undefined) {
                    var res = await getCountryByCode(country);
                    if (res.length > 0 || res.id) {
                        let country = res?.id ? res.id : res[0].id;
                        let formM = {
                            meetingAddress: address,
                            meetingLat: lat,
                            meetingLng: lng,
                            country: [country],
                        }
                        dispatch(updateFormTripMass(formM))
                    } else {

                        dispatch(showAlert(modalContent(t('country_not_supported')), "Warning!", true))

                    }
                }
            }
        )
    }

    return (<>
        <h2 className="text-2xl font-semibold">{t('basic_info')}</h2>
        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>
        <div className="space-y-8">
            <FormItem
                label={t('form_title') + "*"}
            >
                <Input isRequired={true} placeholder={t('trip_name')} value={form?.title ?? ''} onChange={(e) => dispatch(updateFormTrip('title', e.target.value))} />
            </FormItem>


            <div className="relative flex-shrink-0 flex z-10 [ lg:nc-flex-2 ] ">
                <div className="absolute inset-x-0 bottom-0">
                    <DateRangePicker
                        startDate={dateRange.startDate}
                        endDate={dateRange.endDate}
                        focusedInput={focusedInput}
                        onDatesChange={(date) => {
                            setDateRange(date);
                            let dd = {
                                startTripDate: date.startDate,
                                endTripDate: date.endDate,
                            }
                            dispatch(updateFormTripMass(dd));
                        }}
                        onFocusChange={setFocusedInput}
                        numberOfMonths={1}
                        orientation={"horizontal"}
                        startDateId="unique_start_date_id"
                        endDateId="unique_end_date_id"
                        showClearDates
                        noBorder
                        keepOpenOnDateSelect
                        hideKeyboardShortcutsPanel
                    />
                </div>
                <div className="flex flex-col lg:flex-row lg:items-center w-full flex-shrink-0 relative">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-5">
                        <FormItem label={t('start_date') + "*"} className="mr-4">
                            <Input onClick={() => setFocusedInput('startDate')} value={dateRange.startDate ? dateRange.startDate.format("DD MMM YYYY") : ""} readOnly={true} />
                        </FormItem>
                        <FormItem label={t('end_date') + "*"}>
                            <Input onClick={() => setFocusedInput('endDate')} value={dateRange.endDate ? dateRange.endDate.format("DD MMM YYYY") : ""} readOnly={true} />
                        </FormItem>
                    </div>
                </div>

            </div>

            <FormItem
                label={t('meeting_point') + "*"}
                desc={t('meeting_point_desc')}
            >
                <div className="autocomplete-root">
                    <div className="relative">
                        <Input
                            value={place}
                            onChange={(e) => setPlace(e.target.value)}
                            disabled={!ready}
                            className="mt-2"
                            placeholder={t('find_place')}
                        />
                        <div className="absolute inset-y-0 right-2 pr-3 flex items-center pointer-events-none">
                            {placeLoading ? <Loader
                                type="Circles"
                                color="#d24e29"
                                height={25}
                                width={25}
                            /> : <></>}
                        </div>
                    </div>
                    {status === "OK" && (
                        <div className="autocomplete-dropdown-container bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl mt-1 shadow-lg overflow-hidden z-20 relative">
                            {suggestionsData.map((suggestion: any) => (
                                <div
                                    key={suggestion.place_id}
                                    onClick={() => handleSelect(suggestion.description)}
                                    className="flex items-center justify-between py-3 px-4 hover:bg-neutral-100 dark:hover:bg-neutral-700 cursor-pointer transition-colors"
                                >
                                    <span className="text-neutral-700 dark:text-neutral-300 font-medium text-sm">
                                        {suggestion.description}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="mt-4">
                    {lat !== 0 ?
                        <div className="aspect-w-5 aspect-h-5 sm:aspect-h-3">
                            <div className="rounded-xl overflow-hidden">

                                <GoogleMapReact
                                    defaultZoom={15}
                                    center={mapCenter}
                                    defaultCenter={{
                                        lat: defLat !== 0 ? defLat : lat,
                                        lng: defLng !== 0 ? defLng : lng,
                                    }}
                                    onDrag={((map) => {
                                        setLat(map.center.lat());
                                        setLng(map.center.lng());
                                    })}

                                    onDragEnd={(map) => {
                                        meetingAddressUpdate(map.center.lat(), map.center.lng());
                                    }}

                                >
                                    <LocationMarker lat={lat} lng={lng} />
                                </GoogleMapReact>

                            </div>
                        </div> : <div className="flex flex-col lg:flex-row lg:items-center w-full flex-shrink-0 relativ">
                            <Loader
                                type="Circles"
                                color="#d24e29"
                                height={25}
                                width={25}
                            />
                            <span className="ml-4 block mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                                {t('fetching_curr_location')}
                            </span>
                        </div>
                    }
                </div>
            </FormItem>

            <div className="space-y-7">
                <NcInputNumber
                    label={t('min_guest') + "*"}
                    defaultValue={form?.minGuest ?? 1}
                    max={form?.maxGuest ?? 4}
                    className="mb-4"
                    onChange={(e) => {
                        dispatch(updateFormTrip('minGuest', e))
                    }} />
                <NcInputNumber label={t('max_guest') + "*"} defaultValue={form?.maxGuest ?? 4} min={(form?.minGuest && form.minGuest > 0) ? form.minGuest : 1} onChange={(e) => dispatch(updateFormTrip('maxGuest', e))} />
            </div>

        </div>
    </>);
}

export default OpenStepOne;